import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { camelCase, startCase } from 'lodash';

import newIcon from '../assets/icons/document.svg';
import newIconFilled from '../assets/icons/Assignment/newFilled.svg';
import evaluatedIcon from '../assets/icons/evaluated.svg';
import evaluatedIconFilled from '../assets/icons/Assignment/evaluatedFilled.svg';
import missedIcon from '../assets/icons/warning.svg';
import missedIconFilled from '../assets/icons/Assignment/missedFilled.svg';
import pendingIcon from '../assets/icons/timer.svg';
import pendingIconFilled from '../assets/icons/Assignment/pendingFilled.svg';
import submittedIcon from '../assets/icons/submitted.svg';
import submittedIconFilled from '../assets/icons/Assignment/submittedFilled.svg';
import { ICurrentChaptersListResponse, ISort } from '../interface';
import {
  ASSIGNMENT_STATUS,
  BREAKPOINTS,
  TEST_STATUS,
} from '../consts/constants';
import { ChipType, SortBy } from './enum';

dayjs.extend(isBetween);

export function getFullName(firstName: string = '', lastName: string = '') {
  return firstName || lastName
    ? startCase(
        camelCase(`${firstName ? firstName : ''} ${lastName ? lastName : ''}`),
      )
    : '';
}

export const getGreetingMessage = () => {
  if (dayjs().hour() < 12) {
    return 'greetings.goodMorning';
  }
  if (dayjs().hour() >= 12 && dayjs().hour() < 16) {
    return 'greetings.goodAfternoon';
  }
  if (dayjs().hour() >= 16) {
    return 'greetings.goodEvening';
  }
  return '';
};

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export function keysToCamelCase<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => keysToCamelCase(item)) as unknown as T;
  }

  return Object.keys(obj).reduce((acc, key) => {
    const camelCaseKey = toCamelCase(key);
    (acc as any)[camelCaseKey] = keysToCamelCase((obj as any)[key]);
    return acc;
  }, {} as T);
}

export function getFormattedDateTimeString(startTime: string, endTime: string) {
  return `${dayjs(startTime)?.format('ddd')} | ${dayjs(startTime).format('MMM DD')}, ${dayjs(startTime).format('hh:mm')}-${dayjs(endTime).format('hh:mm a')}`;
}
export const getCurrentDateTimeDifference = (toDate: string) => {
  if (!toDate) {
    return { value: 0, key: '' };
  }
  const date1 = dayjs(toDate);
  const date2 = dayjs();

  const days = date1.diff(date2, 'days');
  if (!days || days < 0) {
    const hours = date1.diff(date2, 'hours');
    if (!hours || hours < 0) {
      const minutes = date1.diff(date2, 'minutes');
      return { value: minutes, key: `${minutes}m` };
    }
    return { value: hours, key: `${hours}h` };
  }
  return { value: days, key: `${days}d` };
};

export const getCurrentTimeDifference = (fromDate: string) => {
  const date1 = dayjs(fromDate);
  const date2 = dayjs();

  let day = date2.diff(date1, 'day');
  let hours = date2.diff(date1, 'hours');
  let minutes = date2.diff(date1, 'minutes');

  minutes = minutes - hours * 60;

  if (!day) {
    return `${hours}h ${minutes}m`;
  }
  return `${day}d`;
};

export const addOrdinalSuffix = (i: number) => {
  const j = i % 10,
    k = i % 100;
  if (j === 1 && k !== 11) {
    return i + 'st';
  }
  if (j === 2 && k !== 12) {
    return i + 'nd';
  }
  if (j === 3 && k !== 13) {
    return i + 'rd';
  }
  return i + 'th';
};

export const isDateBetween = (
  dateToCheck: string,
  start: string,
  end: string,
) => {
  return dayjs(dateToCheck).isBetween(dayjs(start), dayjs(end).add(1, 'day'));
};

export const isDateStrictlyBetween = (
  dateToCheck: string,
  start: string,
  end: string,
) => {
  return dayjs(dateToCheck).isBetween(
    dayjs(start).subtract(1, 'day'),
    dayjs(end).add(1, 'day'),
  );
};

export const downloadFile = (url: string, fileName: string) => {
  if (url) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.setAttribute('download', `${fileName}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  }
};

export const getClosestAssignmentDayDifference = (
  assignments: Array<ICurrentChaptersListResponse>,
) => {
  let pendingAssignmentsDates: Array<number> = [];
  if (assignments?.length) {
    pendingAssignmentsDates = assignments
      ?.filter(
        (assignment) =>
          assignment.status?.toLowerCase() ===
          ASSIGNMENT_STATUS.PENDING?.toLowerCase(),
      )
      .map((pendingAssignment) => {
        if (pendingAssignment.assigned_at) {
          const difference = dayjs().diff(
            dayjs(pendingAssignment.assigned_at),
            'days',
          );
          if (difference < 0) {
            return 99999999;
          }
          return difference;
        }
        return 99999999;
      });
  }

  return Math.min(...pendingAssignmentsDates);
};

export const getNoAssignmentsText = (assignmentStatus: string) => {
  if (!assignmentStatus) {
    return '';
  }
  switch (assignmentStatus?.toLowerCase()) {
    case ASSIGNMENT_STATUS.PENDING?.toLowerCase():
      return 'assignments.noPendingAssignmentsAlt';

    case ASSIGNMENT_STATUS.COMPLETED?.toLowerCase():
      return 'assignments.noEvaluatedAssignments';

    case ASSIGNMENT_STATUS.UNDER_REVIEW?.toLowerCase():
      return 'assignments.noSubmittedAssignments';

    case ASSIGNMENT_STATUS.SUBMITTED?.toLowerCase():
      return 'assignments.noSubmittedAssignments';

    case ASSIGNMENT_STATUS.ALL?.toLowerCase():
      return 'assignments.noAssignments';

    default:
      return '';
  }
};

export const isAssignmentMissed = (
  assignmentStatus: string,
  currentChapterId: number | undefined,
  chapterId: number,
) => {
  if (currentChapterId) {
    return currentChapterId !== chapterId &&
      assignmentStatus === ASSIGNMENT_STATUS.PENDING
      ? ASSIGNMENT_STATUS.MISSED
      : assignmentStatus;
  }
  return assignmentStatus;
};

export const sortArray = <T>({ data, sortKey, sortOrder }: ISort<T>) => {
  if (sortOrder === SortBy.NEWEST) {
    const a = data.sort((a: any, b: any) => {
      return new Date(b[sortKey]).getTime() - new Date(a[sortKey]).getTime();
    });
    return a;
  }
  if (sortOrder === SortBy.OLDEST) {
    const a = data.sort((a: any, b: any) => {
      return new Date(a[sortKey]).getTime() - new Date(b[sortKey]).getTime();
    });
    return a;
  }
};

export const getAssignmentStatusIcon = (status: string) => {
  switch (status?.toLowerCase()) {
    case ASSIGNMENT_STATUS.NEW?.toLowerCase():
      return newIcon;
    case ASSIGNMENT_STATUS.PENDING?.toLowerCase():
      return pendingIcon;
    case ASSIGNMENT_STATUS.SUBMITTED?.toLowerCase():
      return submittedIcon;
    case ASSIGNMENT_STATUS.UNDER_REVIEW?.toLowerCase():
      return submittedIcon;
    case ASSIGNMENT_STATUS.COMPLETED?.toLowerCase():
      return evaluatedIcon;
    case ASSIGNMENT_STATUS.MISSED?.toLowerCase():
      return missedIcon;
    default:
      return '';
  }
};

export const getAssignmentStatusFilledIcon = (status: string) => {
  switch (status?.toLowerCase()) {
    case ASSIGNMENT_STATUS.NEW?.toLowerCase():
      return newIconFilled;
    case ASSIGNMENT_STATUS.PENDING?.toLowerCase():
      return pendingIconFilled;
    case ASSIGNMENT_STATUS.SUBMITTED?.toLowerCase():
      return submittedIconFilled;
    case ASSIGNMENT_STATUS.UNDER_REVIEW?.toLowerCase():
      return submittedIconFilled;
    case ASSIGNMENT_STATUS.COMPLETED?.toLowerCase():
      return evaluatedIconFilled;
    case ASSIGNMENT_STATUS.MISSED?.toLowerCase():
      return missedIconFilled;
    default:
      return '';
  }
};

export const getAssignmentChip = (status: string) => {
  switch (status?.toLowerCase()) {
    case ASSIGNMENT_STATUS.COMPLETED?.toLowerCase():
      return ChipType.SUCCESS;

    case ASSIGNMENT_STATUS.MISSED?.toLowerCase():
      return ChipType.DARK;

    case ASSIGNMENT_STATUS.PENDING?.toLowerCase():
      return ChipType.DANGER;

    case ASSIGNMENT_STATUS.UNDER_REVIEW?.toLowerCase():
      return ChipType.INFO;

    case ASSIGNMENT_STATUS.SUBMITTED?.toLowerCase():
      return ChipType.INFO;

    default:
      return '';
  }
};

interface GetAssignmentStatusParams {
  assignmentStatus: string;
  assignmentDueDate: string;
}

/**
 * Determines the status of an assignment based on its properties
 * @param assignmentStatus - Current status of the assignment
 * @param assignmentDueDate - Due date of the assignment
 * @param assignedAt - When the assignment was assigned
 * @returns The appropriate status string
 */
export const getAssignmentStatus = ({ 
  assignmentStatus, 
  assignmentDueDate, 
  assignedAt 
}: GetAssignmentStatusParams & { assignedAt?: string }) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0); 
  const dueDate = assignmentDueDate ? new Date(assignmentDueDate) : null;
  if (dueDate) dueDate.setHours(23, 59, 59, 999); 
  
  const assignedDate = assignedAt ? new Date(assignedAt) : null;
  if (assignedDate) assignedDate.setHours(0, 0, 0, 0);

  const normalizedStatus = assignmentStatus?.toLowerCase();
  const isPending = normalizedStatus === ASSIGNMENT_STATUS.PENDING?.toLowerCase();
  const isSubmitted = normalizedStatus === ASSIGNMENT_STATUS.SUBMITTED?.toLowerCase() || 
  normalizedStatus === ASSIGNMENT_STATUS.UNDER_REVIEW?.toLowerCase();
  const isEvaluated = normalizedStatus === ASSIGNMENT_STATUS.COMPLETED?.toLowerCase();
  
  const isAssignedToday = assignedDate && assignedDate.getTime() === now.getTime();
  const isMissed = isPending && 
                  dueDate && 
                  dueDate < now &&
                  !isSubmitted && 
                  !isEvaluated;
  const isDueToday = isPending && 
                    dueDate && 
                    dueDate.getDate() === now.getDate() &&
                    dueDate.getMonth() === now.getMonth() &&
                    dueDate.getFullYear() === now.getFullYear() &&
                    !isSubmitted && 
                    !isEvaluated;
  if (isAssignedToday && !isSubmitted && !isEvaluated) {
    return ASSIGNMENT_STATUS.NEW;
  }
  
  if (isMissed) {
    return ASSIGNMENT_STATUS.MISSED;
  }
  if (isDueToday) {
    return ASSIGNMENT_STATUS.PENDING;
  }

  switch (normalizedStatus) {
    case ASSIGNMENT_STATUS.PENDING?.toLowerCase():
      return ASSIGNMENT_STATUS.PENDING;

    case ASSIGNMENT_STATUS.UNDER_REVIEW?.toLowerCase():
    case ASSIGNMENT_STATUS.SUBMITTED?.toLowerCase():
      return ASSIGNMENT_STATUS.SUBMITTED;

    case ASSIGNMENT_STATUS.MISSED?.toLowerCase():
      return ASSIGNMENT_STATUS.MISSED;

    case ASSIGNMENT_STATUS.COMPLETED?.toLowerCase():
      return ASSIGNMENT_STATUS.COMPLETED;

    default:
      return assignmentStatus || '';
  }
};

export const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;

  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');

  return `${formattedMinutes}:${formattedSeconds}`;
};

export const composeEmail = (email: string) => {
  window.open(`mailto:${email}`);
};

export const dialCall = (phone: string) => {
  window.open(`tel:${phone}`);
};

export const getTheTimeDifference = (date: string) => {
  if (!date) {
    return { timeDifference: 0, translationKey: '' };
  }
  const daysDifference = dayjs(date).diff(dayjs(), 'days');
  const hoursDifference = dayjs(date).diff(dayjs(), 'hours');
  const minutesDifference = dayjs(date).diff(dayjs(), 'minutes');

  if (daysDifference > 0) {
    return { timeDifference: daysDifference, translationKey: 'daysLeft' };
  }

  if (hoursDifference > 0) {
    return { timeDifference: hoursDifference, translationKey: 'hoursLeft' };
  }

  if (minutesDifference > 0) {
    return { timeDifference: minutesDifference, translationKey: 'minutesLeft' };
  }

  return { timeDifference: 0, translationKey: 'overdue' };
};

export const getPlayerWidthHeight = (width: number) => {
  if (width > BREAKPOINTS.md) {
    return { width: '816', height: '462' };
  }
  if (width <= BREAKPOINTS.md && width > BREAKPOINTS.sm) {
    return { width: '700', height: '390' };
  }
  if (width <= BREAKPOINTS.sm && width > BREAKPOINTS.xs) {
    return { width: '525', height: '292' };
  }
  return { width: '336', height: '192' };
};

export function preloadImage(src: string) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      resolve(img);
    };
    img.onerror = img.onabort = function () {
      reject(src);
    };
    img.src = src;
  });
}

export const truncateNames = ({ str }: { str: string }) => {
  const names = str.split(',');
  if (names.length > 2) {
    return {
      current: `${names.slice(0, 2).join(', ')}...`,
      remainingCount: names.length - 2,
    };
  }

  return {
    current: str,
    remainingCount: 0,
  };
};

export const getAssessmentStatus = ({
  assessmentStatus,
  assessmentToken,
}: {
  assessmentStatus: number;
  assessmentToken: string;
}) => {
  if (assessmentStatus === 2) {
    return TEST_STATUS.COMPLETED;
  }
  if (!assessmentToken) {
    return TEST_STATUS.NOT_ASSIGNED;
  }
  return TEST_STATUS.PENDING;
};

export const minutesToHoursFormat = ({ minutes }: { minutes: number }) => {
  if (!minutes) {
    return '';
  }

  const hourHand = Math.floor(minutes / 60);
  const minuteHand = minutes % 60;
  if (hourHand >= 1 && minuteHand) {
    return `${hourHand} hr ${minuteHand} min`;
  }
  if (hourHand === 1 && !minuteHand) {
    return `${60 * hourHand} min`;
  }
  return `${hourHand ? `${hourHand} hr ` : ''}${minuteHand ? `${minuteHand} min` : ''}`;
};
